"use client";

import { ReactNodeViewRenderer } from "@tiptap/react";
import { mergeAttributes, Range } from "@tiptap/core";

import { AudioBlockView } from "./components/AudioBlockView.js";
import { Audio } from "../Audio/Audio.js";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    audioBlock: {
      setAudioBlock: (attributes: {
        src: string;
        poster: string;
        assetId: string;
        playlistUrl?: string;
      }) => ReturnType;
      setAudioBlockAt: (attributes: {
        src: string;
        poster: string;
        assetId: string;
        playlistUrl?: string;

        pos: number | Range;
      }) => ReturnType;
      setAudioBlockAlign: (align: "left" | "center" | "right") => ReturnType;
      setAudioBlockWidth: (width: number) => ReturnType;
      setAudioBlockCaption: (caption: boolean) => ReturnType;
    };
  }
}

export const AudioBlock = Audio.extend({
  name: "audioBlock",

  group: "block",

  defining: true,

  isolating: true,

  addAttributes() {
    return {
      src: {
        default: "",
        parseHTML: (element) => element.getAttribute("src"),
        renderHTML: (attributes) => ({
          src: attributes.src,
        }),
      },
      poster: {
        default: "",
        parseHTML: (element) => element.getAttribute("poster"),
        renderHTML: (attributes) => ({
          poster: attributes.poster,
        }),
      },
      playlistUrl: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-playlist-url"),
        renderHTML: (attributes) => ({
          "data-playlist-url": attributes.playlistUrl,
        }),
      },
      width: {
        default: "100%",
        parseHTML: (element) => element.getAttribute("data-width"),
        renderHTML: (attributes) => ({
          "data-width": attributes.width,
        }),
      },
      align: {
        default: "center",
        parseHTML: (element) => element.getAttribute("data-align"),
        renderHTML: (attributes) => ({
          "data-align": attributes.align,
        }),
      },
      alt: {
        default: undefined,
        parseHTML: (element) => element.getAttribute("alt"),
        renderHTML: (attributes) => ({
          alt: attributes.alt,
        }),
      },
      caption: {
        default: true,
        parseHTML: (element) => element.getAttribute("data-caption") === "true",
        renderHTML: (attributes) => ({
          "data-caption": attributes.caption,
        }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'audio[src*="tiptap.dev"]:not([src^="data:"]), audio[src*="windows.net"]:not([src^="data:"])',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "audio",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
    ];
  },

  addCommands() {
    return {
      setAudioBlock:
        (attrs) =>
        ({ commands }) => {
          return commands.insertContent({
            type: "audioBlock",
            attrs: {
              src: attrs.src,
              poster: attrs.poster,
              assetId: attrs.assetId,
              playlistUrl: attrs.playlistUrl,
            },
          });
        },

      setAudioBlockAt:
        (attrs) =>
        ({ commands }) => {
          return commands.insertContentAt(attrs.pos, {
            type: "audioBlock",
            attrs: {
              src: attrs.src,
              poster: attrs.poster,
              assetId: attrs.assetId,
              playlistUrl: attrs.playlistUrl,
            },
          });
        },

      setAudioBlockAlign:
        (align) =>
        ({ commands }) =>
          commands.updateAttributes("audioBlock", { align }),

      setAudioBlockWidth:
        (width) =>
        ({ commands }) =>
          commands.updateAttributes("audioBlock", {
            width: `${Math.max(0, Math.min(100, width))}%`,
          }),

      setAudioBlockCaption:
        (caption) =>
        ({ commands }) =>
          commands.updateAttributes("audioBlock", { caption }),
    };
  },

  addNodeView() {
    //@ts-ignore
    return ReactNodeViewRenderer(AudioBlockView);
  },
});

export default AudioBlock;
